import PolicyLayout, { PolicySection, P, UL, OL, LI, Callout } from "./PolicyLayout";

const TOC = [
  { id: "spirit", label: "The spirit" },
  { id: "identity", label: "Identity & credentials" },
  { id: "profiles", label: "Profile quality" },
  { id: "availability", label: "Availability & response time" },
  { id: "sessions", label: "Running a session" },
  { id: "safety", label: "Safety & scope of practice" },
  { id: "money", label: "Money & payouts" },
  { id: "reviews", label: "Reviews & feedback" },
  { id: "prohibited", label: "Prohibited behavior" },
  { id: "enforcement", label: "Enforcement" },
  { id: "contact", label: "Contact" },
];

export default function CoachGuidelines() {
  return (
    <PolicyLayout
      eyebrow="Coaches"
      title="Coach Guidelines"
      updated="February 2026"
      intro="Coachly's reputation is built on the coaches who show up. These guidelines cover what we ask of every approved coach — and what you can expect from Coachly in return."
      toc={TOC}
      testId="policy-coach-guidelines"
    >
      <PolicySection id="spirit" title="1. The spirit of Coachly">
        <P>
          Coachly exists so people can trust an independent coach the way they'd trust
          a good doctor or a great teacher. The bar is: be reliable, be respectful, and
          be great at the thing you claim to coach. Everything below flows from that.
        </P>
      </PolicySection>

      <PolicySection id="identity" title="2. Identity & credentials">
        <UL>
          <LI>Use your real legal name and a recent, clear head-shot as your profile photo.</LI>
          <LI>List credentials you actually hold. Only claim certifications you can produce documentation for on request.</LI>
          <LI>Be truthful about your experience, past clients, and outcomes. Vague marketing language is fine; false claims are not.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="profiles" title="3. Profile quality">
        <UL>
          <LI>A useful bio: 150–500 words on your approach, who you love working with, and what a typical session looks like.</LI>
          <LI>Session type, duration, and pricing must accurately reflect what you deliver.</LI>
          <LI>Keep your <a href="/coach-dashboard" className="text-[#2563EB] hover:underline">availability</a> current. Empty or outdated calendars hurt everyone.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="availability" title="4. Availability & response time">
        <UL>
          <LI>Reply to new booking requests and client messages within <strong>24 hours</strong> during the work week.</LI>
          <LI>Aim for a &lt;10% no-show/late-cancel rate. Repeated last-minute cancellations from your side will lower your profile ranking and can lead to suspension.</LI>
          <LI>Update your timezone if you move — clients rely on it to convert times correctly.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="sessions" title="5. Running a session">
        <OL>
          <LI>Confirm the meeting spot (in person) or the video link (online) with the client at least 12 hours before the session.</LI>
          <LI>Arrive on time. If you're going to be more than 5 minutes late, message the client.</LI>
          <LI>Bring the equipment you promised on your profile.</LI>
          <LI>End on time. Overrunning cuts into the next client's slot.</LI>
          <LI>Mark the session complete in your dashboard right after. This is what triggers your payout and the client's review invite.</LI>
        </OL>
      </PolicySection>

      <PolicySection id="safety" title="6. Safety & scope of practice">
        <Callout tone="amber">
          Only offer coaching in areas you're qualified to coach. If a client presents a
          medical, psychological, or legal issue outside your scope, refer them out. Do
          not diagnose, treat, or prescribe unless you hold the appropriate license.
        </Callout>
        <UL>
          <LI>Do a light intake if physical training is involved. Ask about injuries and health conditions.</LI>
          <LI>Follow the safety rules of your discipline (spotters, warm-ups, protective gear, etc.).</LI>
          <LI>Report anything concerning about a client's safety to us at{" "}
            <a href="mailto:safety@coachly.app" className="text-[#2563EB] hover:underline">safety@coachly.app</a> or via the Report button.
          </LI>
        </UL>
      </PolicySection>

      <PolicySection id="money" title="7. Money & payouts">
        <UL>
          <LI>You must complete Stripe Connect onboarding before you can be paid. Your legal name and government ID on Stripe should match the ones on your Coachly profile.</LI>
          <LI>Coachly's platform fee is 10%. It's shown transparently on every booking.</LI>
          <LI>Payouts release after the session is marked complete. Standard Stripe payout timing applies (typically 1–2 business days to your bank once released).</LI>
          <LI>Attempting to move a booked client off-platform to avoid the fee is a violation of these guidelines and grounds for removal.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="reviews" title="8. Reviews & feedback">
        <UL>
          <LI>You can reply publicly to any review on your profile. Keep it professional even if the review isn't.</LI>
          <LI>Do not offer discounts or rewards in exchange for positive reviews.</LI>
          <LI>You can flag a review that violates our <a href="/trust-safety" className="text-[#2563EB] hover:underline">Trust &amp; Safety Policy</a> and we'll investigate within 3 business days.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="prohibited" title="9. Prohibited behavior">
        <P>These are grounds for immediate removal:</P>
        <UL>
          <LI>Sexual, romantic, or otherwise inappropriate solicitation of a client.</LI>
          <LI>Harassment, discrimination, or threats of any kind.</LI>
          <LI>Coaching under the influence of alcohol or unlawful drugs.</LI>
          <LI>Impersonating another coach, credential, or organization.</LI>
          <LI>Selling illegal or dangerous services (unregulated medical treatments, banned substances, etc.).</LI>
          <LI>Systematically pushing clients off-platform to avoid fees.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="enforcement" title="10. Enforcement">
        <P>
          We investigate reports within 3 business days. Depending on severity we may:
        </P>
        <UL>
          <LI>Give a private warning and coaching on how to fix the issue.</LI>
          <LI>Temporarily hide your profile while we investigate.</LI>
          <LI>Permanently remove your profile and cancel your Coachly account.</LI>
        </UL>
        <P>
          You can appeal any decision by emailing{" "}
          <a href="mailto:appeals@coachly.app" className="text-[#2563EB] hover:underline">appeals@coachly.app</a>{" "}
          within 30 days. A different Coachly reviewer will look at the case.
        </P>
      </PolicySection>

      <PolicySection id="contact" title="11. Contact">
        <P>
          Coach support: <a href="mailto:coaches@coachly.app" className="text-[#2563EB] hover:underline">coaches@coachly.app</a>
        </P>
      </PolicySection>
    </PolicyLayout>
  );
}
